// The service configurations a project can have.
//
// One selection carries three things that used to be entered separately: the
// printed description, the line-to-line voltage the capacity math uses, and the
// phase that decides whether the √3 factor applies.
//
// They were two fields before — a free-text description and a voltage dropdown —
// which could disagree. Setting the dropdown to 240V while typing "120/208V
// three-phase" in the description printed one thing on the report and computed
// another, in the same way an overridden concurrency call used to print TrueLoad's
// contradicting sentence. A single selection removes the disagreement by
// construction.

export type ServicePhase = 'single' | 'three';

export interface ServiceConfiguration {
  /** Stored in `service_voltage_text` and printed on the report. */
  label: string;
  /** Stored in `service_voltage_numeric`. Line-to-line volts. */
  volts: number;
  /** Stored in `service_phase`. Selects the capacity formula. */
  phase: ServicePhase;
}

/** Residential first — it is the overwhelming majority of TrueLoad's use. */
export const SERVICE_CONFIGURATIONS: ServiceConfiguration[] = [
  { label: '120/240V single-phase', volts: 240, phase: 'single' },
  { label: '120/208V single-phase', volts: 208, phase: 'single' },
  { label: '120/208V three-phase', volts: 208, phase: 'three' },
  { label: '277/480V three-phase', volts: 480, phase: 'three' },
];

export const DEFAULT_SERVICE_CONFIGURATION = SERVICE_CONFIGURATIONS[0];

/**
 * The configuration a stored project corresponds to, matched on volts + phase.
 *
 * Matching on those rather than on the stored label is deliberate: the label was
 * free text before this change and may hold anything a user typed, while volts
 * and phase are what the arithmetic actually used. A project that matches nothing
 * falls back to the default so the form still renders a valid selection.
 */
export function resolveServiceConfiguration(
  volts: number | undefined | null,
  phase: string | undefined | null
): ServiceConfiguration {
  const resolvedPhase: ServicePhase = phase === 'three' ? 'three' : 'single';
  return (
    SERVICE_CONFIGURATIONS.find(c => c.volts === volts && c.phase === resolvedPhase) ??
    DEFAULT_SERVICE_CONFIGURATION
  );
}

/**
 * Service capacity in kVA.
 *
 * Single-phase is V×A. Three-phase is √3×V×A — the factor the previous formula
 * omitted, which understated a genuine three-phase service by about 42% and
 * could report a FAIL on a service that was adequate.
 */
export function serviceCapacityKVA(amps: number, volts: number, phase: ServicePhase): number {
  const factor = phase === 'three' ? Math.sqrt(3) : 1;
  return (amps * volts * factor) / 1000;
}
